import { concatLatestFrom } from '@ngrx/operators';
import { PipelineEnvironment } from '@telespot/domain';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { map, skip, switchMap, take, tap } from 'rxjs/operators';
import { combineLatest, from, merge, Observable } from 'rxjs';
import { PaginatedStateEffects } from '@shared/ui';
import { useLocalization } from '@shared/localization';
import type {
  ICountMethodTypesByPipelineQuery,
  ICountPipelinesQuery,
  ICountStepsForPipelineQuery,
  IListPipelinesQuery,
} from '@protocols/use-cases';
import { PipelineData, PipelineState, PipelineStateModel } from '../state/pipeline.state';
import { StagedPipelineDraft, StagedPipelineOverride, StagedPipelinesState } from '../state/staged-pipelines.state';

/*
 * Listing projection of a staged edit to a persisted pipeline: the pre-edit DB
 * summary plus the edited row derived from the staged draft. The listing flow
 * hides the stale DB row, places `override` at its (re-sorted) position, and
 * uses both halves to keep the total count exact even when the edit moves the
 * pipeline in/out of the active search match.
 */
export interface PipelineOverride {
  original: PipelineData;
  override: PipelineData;
}

export const COUNT_PIPELINES_QUERY = new InjectionToken<ICountPipelinesQuery>('COUNT_PIPELINES_QUERY');
export const LIST_PIPELINES_QUERY = new InjectionToken<IListPipelinesQuery>('LIST_PIPELINES_QUERY');
export const COUNT_STEPS_FOR_PIPELINE_QUERY = new InjectionToken<ICountStepsForPipelineQuery>(
  'COUNT_STEPS_FOR_PIPELINE_QUERY'
);
export const COUNT_METHOD_TYPES_BY_PIPELINE_QUERY = new InjectionToken<ICountMethodTypesByPipelineQuery>(
  'COUNT_METHOD_TYPES_BY_PIPELINE_QUERY'
);

@Injectable()
export class PipelineEffects extends PaginatedStateEffects<PipelineData, PipelineStateModel> {
  private readonly countPipelinesQuery = inject<ICountPipelinesQuery>(COUNT_PIPELINES_QUERY);
  private readonly listPipelinesQuery = inject<IListPipelinesQuery>(LIST_PIPELINES_QUERY);
  private readonly countStepsForPipelineQuery = inject<ICountStepsForPipelineQuery>(COUNT_STEPS_FOR_PIPELINE_QUERY);
  private readonly countMethodTypesByPipelineQuery = inject<ICountMethodTypesByPipelineQuery>(
    COUNT_METHOD_TYPES_BY_PIPELINE_QUERY
  );

  constructor(protected readonly pipelineState: PipelineState, private readonly stagedState: StagedPipelinesState) {
    super(pipelineState);
    this.currentLanguage$ = useLocalization().currentLanguage$;
    this.filterByEnvironment(this.pipelineState.environmentFilter$.pipe(skip(1)));
  }

  // Current language for multilingual names
  public currentLanguage$: Observable<string>;

  // ============================================================================
  // Re-exposed selectors from PipelineState and the staging ledger. The ledger
  // stores raw drafts; the catalog-row projection (`toSummary`) is applied here
  // because how a staged draft LISTS is this class's concern.
  // ============================================================================

  public readonly environmentFilter$ = this.pipelineState.environmentFilter$;
  public readonly pendingPipelines$: Observable<PipelineData[]> = this.stagedState.pendingPipelines$.pipe(
    map((drafts) => drafts.map((draft) => this.toSummary(draft, true)))
  );
  public readonly pipelineOverrides$: Observable<Record<string, PipelineOverride>> =
    this.stagedState.pipelineOverrides$.pipe(map((overrides) => this.projectOverrides(overrides)));

  // Backwards-compat alias selectors
  public readonly pipelines$ = this.items$;
  public readonly nextPipelineToken$ = this.nextToken$;
  public readonly previousPipelineTokens$ = this.previousTokens$;
  public readonly pipelinesLoaded$ = this.itemsLoaded$;
  public readonly noPipelines$ = this.noItems$;

  // ============================================================================
  // Re-exposed updaters from PipelineState
  // ============================================================================

  public readonly setEnvironmentFilter = this.pipelineState.setEnvironmentFilter;

  // ============================================================================
  // Backwards-compat effect aliases
  // ============================================================================

  public readonly initializePipelines = this.initializeItems;
  public readonly loadMorePipelines = this.loadMoreItems;

  // ============================================================================
  // Re-exposed updaters from PipelineState
  // ============================================================================

  public readonly setSearchTerm = this.pipelineState.setSearchTerm;
  public readonly changePageSize = this.pipelineState.changePageSize;

  // ============================================================================
  // Effects
  // ============================================================================

  // Environment filter effect
  public readonly filterByEnvironment = this.effect((env$: Observable<PipelineEnvironment | undefined>) =>
    env$.pipe(
      concatLatestFrom(() => [this.pipelineState.searchTerm$]),
      tap(([, searchTerm]) => {
        this.countItems();
        this.listItems({
          searchTerm,
          lastItem: undefined,
        });
      })
    )
  );

  // ============================================================================
  // Abstract method implementations
  // ============================================================================

  protected async countRepository(searchTerm?: string): Promise<number> {
    const snapshot = await combineLatest([
      this.pipelineState.environmentFilter$,
      this.stagedState.pendingPipelines$,
      this.stagedState.pipelineOverrides$,
    ])
      .pipe(take(1))
      .toPromise();
    if (!snapshot) return 0;
    const [environmentFilter, pendingPipelines, pipelineOverrides] = snapshot;
    const dbCount = await this.countPipelinesQuery.execute({ searchTerm, environmentFilter });
    const pendingCount = pendingPipelines
      .map((draft) => this.toSummary(draft, true))
      .filter((p) => this.matchesSearch(p, searchTerm)).length;
    const overrideDelta = Object.values(this.projectOverrides(pipelineOverrides)).reduce(
      (acc, { original, override }) =>
        acc + (this.matchesSearch(override, searchTerm) ? 1 : 0) - (this.matchesSearch(original, searchTerm) ? 1 : 0),
      0
    );
    return dbCount + pendingCount + overrideDelta;
  }

  /**
   * Delete repository item.
   * The pipeline store does not support deletion.
   * Returning false will invalidate any posterior item removal operations.
   * @param _itemId The ID of the item to remove (not used in this case)
   * @returns Promise that lets you know if the operation was successful, always false in this case
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected async deleteRepositoryItem(_itemId: string): Promise<boolean> {
    return false;
  }

  protected listFlow(
    limit: number,
    lastItem: PipelineData | undefined,
    searchTerm: string | undefined
  ): Observable<PipelineData[]> {
    return combineLatest([
      this.currentLanguage$,
      this.pipelineState.environmentFilter$,
      this.stagedState.pendingPipelines$,
      this.stagedState.pipelineOverrides$,
    ]).pipe(
      take(1),
      switchMap(([lang, environmentFilter, pendingPipelines, pipelineOverrides]) => {
        // Paginate by the last item's creation date (newest first).
        const lastItemCreatedAt = lastItem?.createdAt;
        const cursor = lastItemCreatedAt?.getTime();
        const overrideIds = new Set(Object.keys(pipelineOverrides));

        const pendingPage = pendingPipelines
          .map((draft) => this.toSummary(draft, true))
          .filter((p) => this.matchesSearch(p, searchTerm))
          .filter((p) => cursor === undefined || this.cursorValue(p) < cursor)
          .sort((a, b) => this.comparePipelines(a, b));

        const overridesPage = Object.values(this.projectOverrides(pipelineOverrides))
          .map((entry) => entry.override)
          .filter((o) => this.matchesSearch(o, searchTerm))
          .filter((o) => cursor === undefined || this.cursorValue(o) < cursor)
          .sort((a, b) => this.comparePipelines(a, b));

        // Overfetch by |overrides| so that, after dropping stale DB rows whose id
        // is locally edited, at least `limit` DB items remain available to merge.
        const dbLimit = limit + overrideIds.size;

        return from(
          this.listPipelinesQuery.execute({
            lang,
            searchTerm,
            limit: dbLimit,
            lastItemCreatedAt,
            environmentFilter,
          })
        ).pipe(
          map((pipelines) => {
            const dbItems: PipelineData[] = pipelines
              .filter((p) => !overrideIds.has(p.id))
              .map((p) => ({
                id: p.id,
                name: p.name,
                env: p.env,
                createdAt: p.createdAt,
                usedByMethodTypes: undefined, // Will be loaded asynchronously
                numSteps: undefined, // Will be loaded asynchronously
              }));
            return this.mergeSorted(pendingPage, overridesPage, dbItems, limit);
          })
        );
      })
    );
  }

  protected processDeferredAttributes(itemId: string): Observable<Partial<PipelineData>> {
    const methodTypes$ = this.countAttribute(this.countMethodTypesByPipelineQuery.execute(itemId), itemId).pipe(
      map((usedByMethodTypes) => ({ usedByMethodTypes }))
    );
    const steps$ = this.countAttribute(this.countStepsForPipelineQuery.execute(itemId), itemId).pipe(
      map((numSteps) => ({ numSteps }))
    );
    return merge(methodTypes$, steps$);
  }

  // ============================================================================
  // Pending/override merge helpers (mirror LabelEffects)
  // ============================================================================

  /*
   * Catalog-row projection of a staged draft (the ledger's draft is the source
   * of truth): `name`/`env` come straight from the draft, `numSteps` derives
   * from the staged steps, `usedByMethodTypes` is left for the
   * deferred-attribute loader.
   */
  private toSummary(draft: StagedPipelineDraft, pending?: boolean): PipelineData {
    return {
      id: draft.id,
      name: draft.name,
      env: draft.env,
      numSteps: draft.steps.length,
      pending: pending || undefined,
      createdAt: new Date(),
    };
  }

  private projectOverrides(overrides: Record<string, StagedPipelineOverride>): Record<string, PipelineOverride> {
    const projected: Record<string, PipelineOverride> = {};
    for (const [id, entry] of Object.entries(overrides)) {
      // An override edits a persisted pipeline, so it keeps the original's
      // `createdAt` to hold its place in the newest-first ordering.
      projected[id] = {
        original: entry.original,
        override: { ...this.toSummary(entry.draft), createdAt: entry.original.createdAt },
      };
    }
    return projected;
  }

  /**
   * Cursor value mirrors the repository's `createdAt`-descending pagination:
   * the millisecond timestamp, newest first. Pending drafts carry a fresh
   * `createdAt`, so they naturally sort ahead of persisted rows.
   */
  private cursorValue(pipeline: PipelineData): number {
    return (pipeline.createdAt ?? new Date(0)).getTime();
  }

  private comparePipelines(a: PipelineData, b: PipelineData): number {
    return this.cursorValue(b) - this.cursorValue(a);
  }

  private matchesSearch(pipeline: PipelineData, searchTerm: string | undefined): boolean {
    if (!searchTerm) return true;
    const needle = searchTerm.toLowerCase();
    return Object.values(pipeline.name).some((v) => typeof v === 'string' && v.toLowerCase().includes(needle));
  }

  /*
   * 3-way merge of pre-sorted streams. Picks the newest item (smallest
   * `createdAt`-descending compare) across the three indices at each step.
   * Ties resolve pending → overrides → DB; cosmetic since the cursor advances
   * by `createdAt`.
   */
  private mergeSorted(
    pending: PipelineData[],
    overrides: PipelineData[],
    db: PipelineData[],
    limit: number
  ): PipelineData[] {
    const streams: PipelineData[][] = [pending, overrides, db];
    const idx = [0, 0, 0];
    const out: PipelineData[] = [];
    while (out.length < limit) {
      let pick = -1;
      for (let s = 0; s < streams.length; s++) {
        if (idx[s] >= streams[s].length) continue;
        if (pick === -1 || this.comparePipelines(streams[s][idx[s]], streams[pick][idx[pick]]) < 0) {
          pick = s;
        }
      }
      if (pick === -1) break;
      out.push(streams[pick][idx[pick]++]);
    }
    return out;
  }
}
